import { v1 as uuid } from 'uuid';
import { INode, MessagesInterface } from '../../../general/model';
import { WebkitFile } from '@myua/dropzone';
import { sum } from 'lodash';
import { registerFile } from './fileRegistry';
import { JobStatus } from './jobStatus';
export * from './jobStatus';
export type Job = {
  // The actual File object lives in the module-level fileRegistry (keyed by `uuid`),
  // not in the Redux store — keeping only serializable metadata here (MYUA-16384).
  fileName: string;
  fileSize: number;
  fileLastModified: number;
  parent_node_id: number;
  parentIsAssetLab: boolean;
  status: JobStatus;
  uuid: string;
  groupID: string;
  uploadProgress: null | number;
  ellapsedTime: null | number;
  remainingTime: null | number;
  uploadSpeed: null | number; //bytes Per Second
  errorMessage: null | string;
  createdNode: null | INode;
  // Whether an existing file with the same name should be overwritten.
  // Decided up-front (in checkExistantFiles) and passed to create_multipart_upload,
  // because overwrite must be resolved BEFORE the upload starts (new contract).
  overwrite: boolean;
};
export const makeKey = (job: Job): string =>
  [job.fileName, job.fileSize, job.fileLastModified, job.parent_node_id].join('#');

export const makeJob = (
  file: WebkitFile,
  parent_node_id: number,
  groupID: string,
  parentIsAssetLab: boolean,
): Job => {
  const jobUuid = uuid();
  // Register the real File in the module-level registry keyed by the job uuid; the
  // Job stored in Redux keeps only serializable metadata (MYUA-16384).
  registerFile(jobUuid, file);
  return {
    fileName: file.name,
    fileSize: file.size,
    fileLastModified: file.lastModified,
    parent_node_id,
    status: JobStatus.PENDING_VALIDATION,
    uuid: jobUuid,
    groupID,
    uploadProgress: null,
    ellapsedTime: null,
    remainingTime: null,
    parentIsAssetLab,
    uploadSpeed: null,
    createdNode: null,
    errorMessage: null,
    overwrite: false,
  };
};

export type UploaderShape = {
  jobs: Job[];
  groupNames: Record<string, string>;
  // these are waiting for "mapping request" to finish
  pendingJobs: string[];
  checkOverwriteJob: Job | null;
  tagReminderOpen: boolean;
};

export const initialState: UploaderShape = {
  jobs: [],
  groupNames: {},
  pendingJobs: [],
  checkOverwriteJob: null,
  tagReminderOpen: false,
};

export enum UploaderMode {
  REGULAR_NODE = 'REGULAR_NODE',
  CREATIVE_FILE = 'CREATIVE_FILE',
}

export enum EntityType {
  FILE = 'FILE',
  FOLDER = 'FOLDER',
}

// Символы, запрещённые бэкендом в именах файлов и папок (MYUA-16279).
const INVALID_NAME_CHARS = [...':/\\%&{}<>?*$"@`', '\t', '\n', '\r', '\v', '\f', '\0'];
// В тексте уведомления управляющие символы показываем как escape-последовательности.
const INVALID_NAME_CHARS_LABEL = ':/\\%&{}<>?*$"@`\\t\\n\\r\\v\\f\\0';

export const hasInvalidNameChars = (name: string) =>
  INVALID_NAME_CHARS.some((char) => name.includes(char));

export const Messages: MessagesInterface = {
  mapFoldersFail: () => ({
    title: `Server request error`,
    message: `Couldn't create subfolders`,
  }),
  mapFoldersInvalidName: () => ({
    title: `Server request error`,
    message: `Cannot upload file. The file name contains invalid characters: ${INVALID_NAME_CHARS_LABEL}`,
  }),
  validateFileNamesFail: () => ({
    title: `Server request error`,
    message: `Couldn't valiadte file names`,
  }),
};

export const getJobUploadProgress = (j: Job) => {
  switch (j.status) {
    case JobStatus.PENDING_VALIDATION:
    case JobStatus.INVALID_FILE_NAME:
    case JobStatus.ENQUEUED:
    case JobStatus.UPLOAD_FAILURE:
    case JobStatus.BIND_FAILURE:
    case JobStatus.SKIPPED: {
      return 0;
    }
    case JobStatus.TAG_REMINDER:
    case JobStatus.FINISH: {
      return 1;
    }
    case JobStatus.BIND_INPROGRESS: {
      return 0.99;
    }
    case JobStatus.UPLOAD_INPROGRESS:
      return (j.uploadProgress ?? 0) * 0.99; // cap to 99%;
  }
};

export function getAvgProgress(jobs: Job[]) {
  const totalSize = sum(jobs.map((j) => j.fileSize));
  const avgProgress = sum(
    jobs.map((job) => (getJobUploadProgress(job) * job.fileSize) / totalSize),
  );
  return avgProgress;
}
